<template>
  <article v-if="conference" class="self-service-card wide-card conference-detail-card">
    <nav class="conf-detail-crumb">
      <button type="button" class="conf-crumb-link" @click="front.backToConferenceList">会议服务中心</button>
      <span>›</span>
      <strong>会议详细通知</strong>
    </nav>

    <header class="conf-detail-header">
      <span class="conf-detail-badge">PSC ACADEMIC CONFERENCE</span>
      <h3>{{ conference.conferenceTitle }}</h3>
      <div class="conf-detail-meta">
        <span>{{ front.formatDateRange(conference.startDate, conference.endDate) }}</span>
        <span>{{ conference.city || '地点待定' }}</span>
        <span>注册费：{{ userFeeText }}</span>
      </div>
    </header>

    <div v-if="front.conferenceFeedback" class="service-notice warning" role="alert">
      <strong>无法继续报名</strong>
      <span>{{ front.conferenceFeedback }}</span>
    </div>

    <div v-if="deadlinePassed" class="payment-status-panel is-overdue">
      <strong>{{ closedReasonTitle }}</strong>
      <span>{{ closedReasonText }}</span>
    </div>

    <div v-if="isFrozen" class="payment-status-panel is-frozen">
      <strong>报名已冻结 — 会员到期</strong>
      <span>您的会员资格已到期，本场会议报名已暂停推进。续费并完成发票终审后可恢复报名资格。</span>
    </div>

    <section class="conf-detail-section">
      <h4>一、会议主题与内容</h4>
      <p>{{ detailDesc }}</p>
    </section>

    <section class="conf-detail-section">
      <h4>二、会议缴费说明</h4>
      <div class="conf-fee-box">
        <strong>四类收费标准</strong>
        <div
          v-for="row in feeRows"
          :key="row.key"
          class="conf-fee-row"
          :class="{ 'is-mine': row.isUserType }"
        >
          <span>
            {{ row.label }}
            <em v-if="row.isUserType">您的类型</em>
          </span>
          <span>{{ row.amount > 0 ? `¥${row.amount}` : '--（关闭）' }}</span>
        </div>
      </div>
      <p>
        会议注册费支持银行线下汇款和公务卡现场支付。线下汇款需在门户提交汇款凭证与电子发票；公务卡现场支付登记后直接通过，无需上传上述材料。
        公务卡类报名可随时填写和修改参会信息；线下汇款仍按凭证、发票审核流程逐步开放参会能力。
      </p>
      <p class="conf-deadline-note">
        缴费截止日期：<strong>{{ front.formatDate(conference.paymentDeadline) || '待定' }}</strong>，
        学术摘要截止日期：<strong>{{ front.formatDate(conference.abstractDeadline) || '待定' }}</strong>
      </p>
    </section>

    <section v-if="accommodationConfig" class="conf-detail-section conf-accommodation-section">
      <h4>三、住宿安排</h4>
      <p class="conf-accommodation-config">{{ accommodationConfig }}</p>
      <small>报名后请在参会信息中选择男单间、男双人间、女单间、女双人间或自主安排。</small>
    </section>

    <section class="conf-detail-section">
      <h4>会议资料下载</h4>
      <div class="conf-material-grid">
        <div class="conf-material-card">
          <strong>会议通知（不盖章）</strong>
          <small>查看会议通知全文</small>
          <button type="button" class="ghost-action compact" @click="front.openConferenceNotice(conference)">查看通知</button>
        </div>

        <div class="conf-material-card" :class="{ 'is-unlocked': paymentConfirmed }">
          <strong>盖章会议通知 PDF</strong>
          <small>{{ paymentConfirmed ? '盖中国古生物学会电子章的正式通知' : '缴费确认后解锁下载' }}</small>
          <a
            v-if="paymentConfirmed && sealedNoticeUrl"
            class="green-action compact"
            href="javascript:;"
            @click.prevent="front.openSecureFile(sealedNoticeUrl, '盖章会议通知.pdf')"
          >下载 PDF</a>
          <button v-else type="button" class="locked-action" disabled>
            {{ paymentConfirmed ? '管理员尚未上传' : '需缴费确认' }}
          </button>
        </div>

        <div class="conf-material-card" :class="{ 'is-unlocked': paymentConfirmed }">
          <strong>会议论文摘要模板</strong>
          <small>{{ paymentConfirmed ? '论文摘要格式模板 Word 文档' : '缴费确认后解锁下载' }}</small>
          <a
            v-if="paymentConfirmed && abstractTemplateUrl"
            class="green-action compact"
            href="javascript:;"
            @click.prevent="front.openSecureFile(abstractTemplateUrl, '会议论文摘要模板.docx')"
          >下载 Word</a>
          <button v-else type="button" class="locked-action" disabled>
            {{ paymentConfirmed ? '管理员尚未上传' : '需缴费确认' }}
          </button>
        </div>
      </div>
    </section>

    <footer class="conf-detail-footer">
      <button type="button" class="ghost-action compact" @click="front.backToConferenceList">返回会议列表</button>
      <div class="conf-detail-footer__actions">
        <template v-if="isFrozen">
          <button
            type="button"
            class="gold-action compact"
            @click="front.goServicesTab('member')"
          >
            立即续费
          </button>
        </template>
        <template v-else>
          <button
            v-if="registration && front.registrationCanFillAttendee(registration)"
            type="button"
            class="green-action compact"
            @click="front.openAttendeeForm(registration)"
          >
            填写参会信息
          </button>
          <button
            v-if="showContinuePayment"
            type="button"
            class="primary-action compact"
            @click="front.openConferencePaymentFlow()"
          >
            {{ continuePaymentLabel }}
          </button>
          <button
            v-else-if="!registration"
            type="button"
            class="primary-action compact"
            :disabled="deadlinePassed"
            @click="front.startPaymentFromDetail"
          >
            {{ closedActionLabel }}
          </button>
        </template>
      </div>
    </footer>
  </article>
</template>

<script>
export default {
  name: 'ConferenceDetailCard',
  inject: ['front'],
  computed: {
    conference() {
      return this.front.selectedConference
    },
    registration() {
      return this.conference ? this.front.registrationByConferenceId(this.conference.conferenceId) : null
    },
    feeRows() {
      return this.front.conferenceFeeRows(this.conference)
    },
    detailDesc() {
      if (!this.conference) {
        return ''
      }
      return this.conference.description
        || this.conference.intro
        || `${this.conference.city || '地点待定'}举办。欢迎广大古生物学及相关学科工作者踊跃参会，交流最新研究成果。`
    },
    userFeeText() {
      const amount = this.front.conferenceUserFeeAmount(this.conference)
      const feeType = this.front.currentConferenceFeeTypeKey
      if (amount == null) {
        return '按身份匹配'
      }
      if (feeType) {
        return `¥ ${amount} 元（${this.front.conferenceFeeTypeLabel(feeType)}）`
      }
      return `¥ ${amount} 元起`
    },
    deadlinePassed() {
      return !this.front.isConferenceRegistrationOpen(this.conference)
    },
    closedReasonTitle() {
      if (this.front.isConferenceEnded(this.conference)) {
        return '会议已结束'
      }
      if (this.front.isConferencePaymentDeadlinePassed(this.conference)) {
        return '缴费已截止'
      }
      return '报名未开放'
    },
    closedReasonText() {
      if (this.front.isConferenceEnded(this.conference)) {
        return '本场会议已按会期结束，不再接受新报名。'
      }
      if (this.conference && this.conference.paymentDeadline) {
        return `截止日期：${this.front.formatDate(this.conference.paymentDeadline)}`
      }
      return '当前无法发起新的会议注册费缴纳'
    },
    closedActionLabel() {
      if (this.front.isConferenceEnded(this.conference)) {
        return '会议已结束'
      }
      if (this.front.isConferencePaymentDeadlinePassed(this.conference)) {
        return '缴费已截止'
      }
      if (this.deadlinePassed) {
        return '报名未开放'
      }
      return '立即缴纳注册费报名'
    },
    isFrozen() {
      return this.registration && this.registration.frozenDueToExpiry === '1'
        && this.registration.paymentMethod !== 'OFFICIAL_CARD_ONSITE'
    },
    paymentConfirmed() {
      return this.front.isConferencePaymentConfirmed(this.conference)
    },
    sealedNoticeUrl() {
      const materials = this.front.selectedMaterials || {}
      return materials.sealedNoticeUrl || (this.paymentConfirmed && this.conference.sealedNoticeUrl) || ''
    },
    abstractTemplateUrl() {
      const materials = this.front.selectedMaterials || {}
      return materials.abstractTemplateUrl || (this.paymentConfirmed && this.conference.abstractTemplateUrl) || ''
    },
    accommodationConfig() {
      return String((this.conference && this.conference.accommodationConfig) || '').trim()
    },
    showContinuePayment() {
      if (!this.registration || this.paymentConfirmed || this.isFrozen) {
        return false
      }
      return ['UNPAID', 'VOUCHER_REJECTED', 'VOUCHER_REVIEW', 'INVOICE_PENDING', 'INVOICE_REJECTED', 'INVOICE_OVERDUE', 'INVOICE_REVIEW'].includes(this.registration.paymentStatus)
    },
    continuePaymentLabel() {
      const status = this.registration && this.registration.paymentStatus
      if (['INVOICE_PENDING', 'INVOICE_REJECTED', 'INVOICE_OVERDUE'].includes(status)) {
        return '上传发票'
      }
      if (status === 'VOUCHER_REJECTED') {
        return '重新提交凭证'
      }
      if (status === 'VOUCHER_REVIEW' || status === 'INVOICE_REVIEW') {
        return '查看缴费进度'
      }
      return '继续缴费'
    }
  }
}
</script>

<style scoped>
.conf-detail-crumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--subtle);
}

.conf-crumb-link {
  border: 0;
  background: transparent;
  color: var(--subtle);
  cursor: pointer;
  padding: 0;
  font-size: 12px;
}

.conf-crumb-link:hover {
  color: var(--primary);
}

.conf-detail-crumb strong {
  color: var(--primary);
}

.conf-detail-header {
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 18px;
}

.conf-detail-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f5e0ba;
  color: #241a03;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.conf-detail-header h3 {
  margin: 10px 0 8px;
  font-size: 24px;
  color: var(--primary);
}

.conf-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  color: var(--subtle);
  font-size: 12px;
}

.conf-detail-section {
  display: grid;
  gap: 10px;
}

.conf-detail-section h4 {
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
  color: var(--primary);
  font-size: 14px;
}

.conf-detail-section p {
  margin: 0;
  color: var(--subtle);
  font-size: 13px;
  line-height: 1.75;
}

.conf-fee-box {
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f8fafc;
  display: grid;
  gap: 6px;
}

.conf-fee-box > strong {
  color: var(--primary);
  font-size: 13px;
}

.conf-fee-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid var(--border);
  font-size: 12px;
  color: var(--subtle);
}

.conf-fee-row.is-mine {
  background: #f0fdf4;
  border-color: #86efac;
  color: var(--primary);
  font-weight: 700;
}

.conf-fee-row em {
  margin-left: 6px;
  font-style: normal;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
}

.conf-deadline-note {
  padding: 8px 10px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 12px !important;
}

.conf-accommodation-section {
  padding: 14px;
  border: 1px solid #e5d7b8;
  border-left: 4px solid #c5a059;
  border-radius: 8px;
  background: #fbfaf7;
}

.conf-accommodation-config {
  color: #334155 !important;
  white-space: pre-line;
  word-break: break-word;
}

.conf-accommodation-section small {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.6;
}

.conf-material-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.conf-material-card {
  display: grid;
  gap: 6px;
  justify-items: center;
  text-align: center;
  padding: 14px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f8fafc;
}

.conf-material-card.is-unlocked {
  background: rgba(240, 253, 244, 0.45);
  border-color: #86efac;
}

.conf-material-card strong {
  font-size: 12px;
  color: #475569;
}

.conf-material-card small {
  font-size: 10px;
  color: #94a3b8;
}

.conf-material-card .ghost-action,
.conf-material-card .green-action,
.conf-material-card .locked-action {
  width: 100%;
  margin-top: 4px;
}

.locked-action {
  border: 0;
  border-radius: 6px;
  min-height: 32px;
  background: #e2e8f0;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
  cursor: not-allowed;
}

.green-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  min-height: 34px;
  padding: 0 12px;
  background: #16a34a;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}

.conf-detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.conf-detail-footer__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.payment-status-panel {
  padding: 12px 14px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
}

.payment-status-panel.is-overdue {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #c2410c;
}

.payment-status-panel.is-frozen {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #c2410c;
}

@media (max-width: 860px) {
  .conf-material-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .conf-detail-header h3 {
    font-size: 20px;
  }
}
</style>
